import { Callout, Chip, IconButton, Icons, Kbd, LockBadge, SegmentedControl, TextButton } from "../../../ds";
import { ChangesetConflictDTO, ChangesetMergeDTO } from "../models/ChangeRequestDTO";
import { Fragment, useMemo } from "react";
import { Resolution, Resolutions, conflictKey, isLineConflict } from "../utils/conflicts";
import { ReviewKindTag, kindLabels, outcomeLabels } from "./ReviewKindBadge";
import { SideLabels, sideLabels } from "../utils/labels";

import { ContentDiff } from "./ContentDiff";
import { renderPage } from "../../../astromark";
import { DateTimeHelper } from "../../../lib/utils/DateTimeHelper";
import { LockMark } from "../../locks/models/LockDTO";
import { MergedContent } from "./MergedContent";
import { PageConflicts } from "./RebaseConflicts";
import { ReviewItem } from "../utils/reviewModel";

/** Where the current body of the item's page is. `unavailable`: the change request is closed, so the page no longer shows "before". */
export type CurrentContentState = "loading" | "loaded" | "error" | "unavailable";

/**
 * How the body is shown: the author's changes as a markdown diff, what changed on the page since the proposal (a stale page that kept
 * its base), what an update would leave on the page (the two merged, conflicts shown as both versions), what the last update changed
 * in the proposal since the reader last saw it, the rendered page, or diff and page side by side. No raw-source view: the diff already
 * shows the markdown line by line (all added for a new page, all removed for a deleted one).
 */
export type ReviewView = "changes" | "since" | "merged" | "sinceUpdate" | "preview" | "split";

const viewLabels: Record<ReviewView, string> = {
    changes: "Changes", since: "Changes since", merged: "Merged", sinceUpdate: "Since the update", preview: "Preview", split: "Side by side",
};

interface IReviewItemPaneProps {
    item: ReviewItem;
    spaceId?: string;
    /** The page's body now (existing pages only). */
    currentContent?: string;
    contentState: CurrentContentState;
    isOpen: boolean;
    lockMark?: LockMark;
    /** What an update could not merge on this page. */
    conflicts?: ChangesetConflictDTO[];
    /** The page's update op merged with the edits made since (what an update would leave), when the page changed since the proposal. */
    merge?: ChangesetMergeDTO;
    resolutions?: Resolutions;
    /** The author of an open change request settles conflicts; everyone else reads them. */
    canResolve?: boolean;
    onResolve?: (key: string, resolution: Resolution | undefined) => void;
    /** The words for the two sides of a conflict, by who reads. */
    labels?: SideLabels;
    /** The proposed body at the revision the reader last saw, when the last update changed it (`null`: it was cleared). */
    previousContent?: string | null;
    position: number;
    total: number;
    personLabel: (id: string | null | undefined) => string;
    /** The view the reviewer picked, kept while they move between pages; absent: preview for a new page, changes otherwise. */
    view?: ReviewView;
    onViewChange: (view: ReviewView) => void;
    onPrevious?: () => void;
    onNext?: () => void;
}

/** Rules as text for the diff; no rules (absent or `{}`) is empty, so adding the first ones reads as additions only. */
const json = (value: Record<string, unknown> | null | undefined) =>
    value && Object.keys(value).length ? JSON.stringify(value, null, 2) : "";

/** The page as the reader renders it: the same renderer (`renderPage`, sanitized) and the reader's body styles (`ark-prose`). */
function Preview(props: { markdown: string }) {
    const html = useMemo(() => ({ __html: renderPage({ body: props.markdown }).html }), [props.markdown]);
    if (!props.markdown.trim()) return <div className="cr-diff-empty">The page is empty.</div>;
    return <div className="cr-preview ark-prose" dangerouslySetInnerHTML={html} />;
}

const defaultLabels = sideLabels({ isAuthor: true });

/** One changed page: what changes (diff, rendered preview, or both side by side), and what an approve would run into. */
export function ReviewItemPane(props: IReviewItemPaneProps) {
    const { item, contentState } = props;
    const labels = props.labels ?? defaultLabels;
    const isNew = item.kind === "created";
    const isDeleted = item.kind === "deleted";
    // a deleted page's body is only there to show while the change request is open
    const hasBody = item.proposedContent !== undefined || (isDeleted && props.isOpen);
    // the page as the author found it (kept by the server since change requests keep bases), and as it is now
    const baseContent = item.base ? item.base.content ?? "" : undefined;
    const hasBase = baseContent !== undefined;
    const hasCurrent = contentState === "loaded" && props.currentContent !== undefined;
    // the author's changes diff base → proposal; without a base, the page as it is now stands in for it (later edits included)
    const canDiff = isNew || (isDeleted ? hasCurrent || hasBase : hasBase || hasCurrent);
    // what other people changed on a stale page after the proposal: base → now
    const canSince = props.isOpen && item.stale && hasBase && hasCurrent && !isDeleted && !isNew;
    // what an update would leave on the page: the server's merge of the author's body with the page's edits since
    const conflicts = props.conflicts ?? [];
    const lineConflicts = conflicts.filter(isLineConflict);
    const otherConflicts = conflicts.filter((c) => !isLineConflict(c));
    const canMerged = props.isOpen && !isDeleted && !isNew && props.merge?.content != null;
    // what the last update changed in the proposal since the reader last read it
    const canSinceUpdate = props.isOpen && !isDeleted && props.previousContent !== undefined && (props.previousContent ?? "") !== (item.proposedContent ?? "");
    const resolutions = props.resolutions ?? {};
    const onResolve = props.onResolve ?? (() => undefined);

    // a page with line conflicts opens on them; one the last update changed, on what changed
    const view: ReviewView = props.view ?? (isNew ? "preview" : canMerged && lineConflicts.length ? "merged" : canSinceUpdate ? "sinceUpdate" : "changes");
    // without a "before" (closed change request with no base, current version not loaded) there is only the preview
    const effectiveView: ReviewView = !canDiff ? "preview"
        : view === "since" && !canSince ? "changes"
        : view === "merged" && !canMerged ? "changes"
        : view === "sinceUpdate" && !canSinceUpdate ? "changes"
        : view;
    const views: ReviewView[] = [
        "changes" as const,
        ...(canSince ? ["since" as const] : []),
        ...(canMerged ? ["merged" as const] : []),
        ...(canSinceUpdate ? ["sinceUpdate" as const] : []),
        "preview" as const,
        "split" as const,
    ];
    // only a view that compares against the page now waits for it
    const waitsForCurrent = !isNew && contentState === "loading" && (isDeleted || !hasBase || effectiveView === "since");

    // a deleted page shows what goes: the page now, else as the author found it
    const removed = hasCurrent ? props.currentContent ?? "" : baseContent ?? "";
    const before = isNew ? "" : isDeleted ? removed : hasBase ? baseContent ?? "" : props.currentContent ?? "";
    const after = isDeleted ? "" : item.proposedContent ?? "";
    // what the page looks like after the change (a deleted page: what is lost)
    const shown = isDeleted ? removed : after;
    const unresolvedLines = lineConflicts.filter((c) => !resolutions[conflictKey(c)]).length;
    const comparedWith = isNew || isDeleted || effectiveView === "preview" || effectiveView === "since" ? undefined
        : effectiveView === "merged"
            ? lineConflicts.length
                ? `The page as an update would leave it: the edits made since and the author's merged; where both changed the same lines, both versions${unresolvedLines === 0 ? ", with the choices made" : ""}.`
                : "The page as an update would leave it, compared with the page as it is now."
            : effectiveView === "sinceUpdate"
                ? "What the last update changed in the proposal, compared with the revision you last read."
                : hasBase ? "Compared with the page as the author found it." : "Compared with the page as it is now, later edits included.";
    const editor = props.personLabel(item.modifiedBy);
    const editedBy = editor === "unknown" ? "" : ` by ${editor}`;
    const viewLabel = (v: ReviewView) => (v === "merged" && lineConflicts.length ? `Merged · ${unresolvedLines || "✓"}` : viewLabels[v]);

    // the line under the title, as the page header shows author · date
    const subinfo: React.ReactNode[] = [];
    if (item.previousTitle !== undefined) subinfo.push(<span>Renamed from “{item.previousTitle}”</span>);
    if (item.move) {
        subinfo.push(
            <span>
                {item.move.index === 0 ? `Moved to the top of “${item.move.parentTitle}”`
                    : item.move.index !== undefined ? `Moved to position ${item.move.index + 1} in “${item.move.parentTitle}”`
                    : `Moved under “${item.move.parentTitle}”`}
            </span>
        );
    }
    if (props.isOpen && item.stale && !item.missing && item.modifiedAt) {
        subinfo.push(<span>Changed {DateTimeHelper.formatDateTimeAsTimeAgo(item.modifiedAt)}{editedBy}</span>);
    }
    if (item.outcome) {
        const tone = item.outcome === "applied" ? "success" : item.outcome === "failed" ? "danger" : "neutral";
        subinfo.push(<Chip size="sm" tone={tone} uppercase={false}>{outcomeLabels[item.outcome]}</Chip>);
    }
    if (item.pageId !== undefined && !item.missing && !isDeleted && props.spaceId) {
        subinfo.push(
            <a className="cr-external" href={`/spaces/${props.spaceId}/pages/${item.pageId}`} target="_blank" rel="noreferrer">
                Open current page <span aria-hidden="true">↗</span>
            </a>
        );
    }

    const sinceLink = canSince && effectiveView !== "since"
        ? <> <TextButton className="cr-inline-link" text="Show changes since" onClick={() => props.onViewChange("since")} /></>
        : null;

    return (
        <section className="cr-pane" aria-label={`${kindLabels[item.kind]}: ${item.title}`}>
            <div className="cr-pane-head">
                <div className="cr-pane-titles">
                    <div className="cr-pane-title-line">
                        <h2 className="cr-pane-title">
                            {item.title}
                            {props.lockMark ? <LockBadge state={props.lockMark.own ? "own" : "inherited"} by={props.lockMark.lockTitle} size="md" /> : null}
                        </h2>
                        <ReviewKindTag kind={item.kind} />
                    </div>
                    {subinfo.length ? (
                        <div className="cr-meta">
                            {subinfo.map((part, i) => (
                                <Fragment key={i}>
                                    {i > 0 ? <span className="cr-meta-dot" aria-hidden="true">·</span> : null}
                                    {part}
                                </Fragment>
                            ))}
                        </div>
                    ) : null}
                </div>
                <div className="cr-pane-nav">
                    <span className="cr-pane-position">{props.position} of {props.total}</span>
                    <IconButton icon={<Icons.arrowUp size={16} />} size={32} onClick={props.onPrevious} isDisabled={!props.onPrevious} aria-label="Previous page" title="Previous page (k)" />
                    <IconButton icon={<Icons.arrowDown size={16} />} size={32} onClick={props.onNext} isDisabled={!props.onNext} aria-label="Next page" title="Next page (j)" />
                    <span className="cr-pane-keys" aria-hidden="true"><Kbd>k</Kbd>/<Kbd>j</Kbd></span>
                </div>
            </div>

            {props.isOpen && item.stale && !item.missing ? (
                <Callout tone={conflicts.length ? "danger" : "info"}>
                    <strong>This page changed after the change request was made{editedBy}.</strong>
                    {conflicts.length
                        ? ` ${conflicts.length === 1 ? "One change conflicts" : `${conflicts.length} changes conflict`} with those edits${lineConflicts.length ? ": Merged shows where" : ""}.${
                            props.canResolve ? (unresolvedLines ? " Pick a side for each, or edit, then Update." : "") : labels.decides ? ` ${labels.decides}.` : ""}`
                        : props.merge
                            ? ` The edits merge with the author's without conflicts; Merged shows the result.`
                            : " It can't be approved until the author updates the change request."}
                    {hasBase && !conflicts.length ? " Changes shows only what the author changed; Changes since shows what changed on the page after." : ""}
                    {sinceLink}
                </Callout>
            ) : null}
            {item.missing ? <Callout tone="warning"><strong>This page no longer exists in the space.</strong> The change to it cannot be applied.</Callout> : null}
            {item.outcome === "failed" ? <Callout tone="danger"><strong>This change failed:</strong> {item.error ?? "no details"}</Callout> : null}
            {item.outcome === "skipped" ? <Callout tone="info">Not applied: Ark stopped at an earlier change that failed.</Callout> : null}
            {isDeleted && item.childCount > 0 ? (
                <Callout tone="warning">This page has {item.childCount} subpage{item.childCount === 1 ? "" : "s"}.</Callout>
            ) : null}
            {!props.isOpen && !isNew && hasBody && !hasBase ? (
                <Callout tone="info">This change request is closed, so the page has moved on: the proposed content is shown without a diff.</Callout>
            ) : null}
            {contentState === "error" && !isNew && hasBody && !canDiff ? (
                <Callout tone="info">The current version of the page could not be loaded: the proposed content is shown without a diff.</Callout>
            ) : null}

            {otherConflicts.length || (lineConflicts.length && !canMerged) ? (
                // line conflicts are shown in the merged content; without it (an older server), as cards too
                <PageConflicts conflicts={canMerged ? otherConflicts : conflicts} resolutions={resolutions} canResolve={!!props.canResolve} onResolve={onResolve} labels={labels} />
            ) : null}

            {item.previousTitle !== undefined ? (
                <div className="cr-field">
                    <h4 className="cr-field-label">Title</h4>
                    <div className="cr-title-change">
                        <del>{item.previousTitle}</del>
                        <span className="cr-title-arrow" aria-hidden="true"><Icons.arrowRight size={14} /></span>
                        <ins>{item.title}</ins>
                    </div>
                </div>
            ) : null}

            {hasBody ? (
                <div className="cr-field">
                    <div className="cr-field-head">
                        <h4 className="cr-field-label">{isDeleted ? "Content that will be removed" : "Content"}</h4>
                        <SegmentedControl<ReviewView>
                            aria-label="View"
                            size="sm"
                            className="cr-view-switch"
                            value={effectiveView}
                            onChange={props.onViewChange}
                            options={views.map((v) => ({ value: v, label: viewLabel(v), disabled: v !== "preview" && !canDiff }))}
                        />
                    </div>
                    {comparedWith ? <p className="cr-caption">{comparedWith}</p> : null}
                    {item.proposedContent === null ? <Callout tone="warning">The change request clears this page's content.</Callout> : null}
                    {waitsForCurrent ? (
                        <div className="cr-diff-empty">Loading the current version…</div>
                    ) : effectiveView === "changes" ? (
                        <ContentDiff before={before} after={after} label="Content" />
                    ) : effectiveView === "since" ? (
                        <ContentDiff before={baseContent ?? ""} after={props.currentContent ?? ""} label="Content since the proposal" />
                    ) : effectiveView === "sinceUpdate" ? (
                        <ContentDiff before={props.previousContent ?? ""} after={after} label="Content since the update" />
                    ) : effectiveView === "merged" && props.merge ? (
                        lineConflicts.length
                            ? <MergedContent merge={props.merge} conflicts={lineConflicts} resolutions={resolutions} canResolve={!!props.canResolve} onResolve={onResolve} labels={labels} />
                            : <ContentDiff before={props.currentContent ?? ""} after={props.merge.content ?? ""} label="Merged content" />
                    ) : effectiveView === "split" ? (
                        <div className="cr-split">
                            <ContentDiff before={before} after={after} label="Content" />
                            <Preview markdown={shown} />
                        </div>
                    ) : (
                        <Preview markdown={shown} />
                    )}
                </div>
            ) : null}

            {item.proposedRules ? (
                <div className="cr-field">
                    <h4 className="cr-field-label">Metadata rules</h4>
                    {(props.isOpen || item.base) && !isNew ? (
                        <ContentDiff before={json(item.previousRules)} after={json(item.proposedRules)} label="Rules" format="JSON" />
                    ) : (
                        <pre className="cr-source" aria-label="Proposed rules">{json(item.proposedRules)}</pre>
                    )}
                </div>
            ) : null}

            {item.uploads.length ? (
                <div className="cr-field">
                    <h4 className="cr-field-label">Files</h4>
                    <ul className="cr-files">
                        {item.uploads.map((name) => (
                            <li key={name}>
                                <Chip size="sm" uppercase={false} icon={<Icons.fileText size={12} />}>
                                    <span className="ark-mono">{name}</span>
                                </Chip>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </section>
    );
}
